import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

interface LoginFormProps {
  onSwitchToRegister: () => void;
  onLoginSuccess?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onSwitchToRegister,
  onLoginSuccess,
}) => {
  const { signIn, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = "Email harus diisi";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Format email tidak valid";
    }

    if (!formData.password) {
      newErrors.password = "Password harus diisi";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    const result = await signIn(formData.email, formData.password);

    if (result.success) {
      // Trigger login success callback if provided
      if (onLoginSuccess) {
        onLoginSuccess();
      } else {
        // Fallback: explicitly navigate to dashboard after successful login
        navigate("/dashboard");
      }
    } else {
      if (result.error) {
        setErrors({ submit: result.error });
      }
    }

    setLoading(false);
  };

  const handleResetPassword = async () => {
    if (!formData.email) {
      setErrors({ email: "Masukkan email terlebih dahulu" });
      return;
    }

    setIsResettingPassword(true);
    await resetPassword(formData.email);
    setIsResettingPassword(false);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  // Check if form is valid for button state
  const isFormValid =
    formData.email.trim() !== "" && formData.password.trim() !== "";

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Email Field */}
      <div>
        <label
          htmlFor="email"
          className="block text-sm font-medium text-[#373e46] mb-2"
        >
          Email
        </label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={e => handleInputChange("email", e.target.value)}
          className={`h-12 rounded-xl border-2 ${
            errors.email ? "border-red-500" : "border-[#e3e6eb]"
          } focus:border-[#6b2cfe] transition-colors`}
          placeholder="Tulis email"
        />
        {errors.email && (
          <p className="mt-1 text-sm text-red-500">{errors.email}</p>
        )}
      </div>

      {/* Password Field */}
      <div>
        <label
          htmlFor="password"
          className="block text-sm font-medium text-[#373e46] mb-2"
        >
          Password
        </label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onChange={e => handleInputChange("password", e.target.value)}
            className={`h-12 rounded-xl border-2 pr-12 ${
              errors.password ? "border-red-500" : "border-[#e3e6eb]"
            } focus:border-[#6b2cfe] transition-colors`}
            placeholder="Ketik password"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            {showPassword ? (
              <EyeOffIcon className="w-5 h-5" />
            ) : (
              <EyeIcon className="w-5 h-5" />
            )}
          </button>
        </div>
        {errors.password && (
          <p className="mt-1 text-sm text-red-500">{errors.password}</p>
        )}
      </div>

      {/* Forgot Password Link */}
      <div className="text-right">
        <button
          type="button"
          onClick={handleResetPassword}
          disabled={isResettingPassword}
          className="text-[#6b2cfe] text-sm font-medium hover:underline disabled:opacity-50"
        >
          {isResettingPassword ? "Mengirim..." : "Lupa Password?"}
        </button>
      </div>

      {/* Submit Error */}
      {errors.submit && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-600">{errors.submit}</p>
        </div>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={loading || !isFormValid}
        className={`w-full h-12 font-semibold rounded-xl transition-all duration-300 ${
          isFormValid && !loading
            ? "bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white hover:shadow-lg"
            : "bg-gray-300 text-gray-500 cursor-not-allowed"
        }`}
      >
        {loading ? (
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
            Memproses...
          </div>
        ) : (
          "Masuk"
        )}
      </Button>

      {/* Switch to Register */}
      <div className="text-center pt-4">
        <p className="text-sm text-[#373e46] mb-3">Belum memiliki akun?</p>
        <Button
          type="button"
          onClick={onSwitchToRegister}
          className="w-full h-12 bg-[#e4e6ff] text-[#6b2cfe] font-medium rounded-xl hover:bg-[#d1d5ff] transition-all duration-300"
        >
          Daftar Sekarang
        </Button>
      </div>
    </form>
  );
};
